import React, { useState } from 'react';
import { createInvite } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar, Euro, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function SendInviteModal({
  open,
  onOpenChange,
  request,
  interest,
  profile,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fee: request?.budgetAmount || '',
    callTime: request?.startTime || '',
    notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createInvite({
        requestId: request.id,
        interestId: interest.id,
        talentProfileId: profile.id,
        fee: parseFloat(formData.fee) || null,
        callTime: formData.callTime || null,
        notes: formData.notes || null,
      });
      toast.success(`Invite sent to ${profile.displayName}`);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  if (!request || !interest || !profile) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto s8-glow-border">
        <DialogHeader>
          <DialogTitle className="text-lg text-foreground">Send Invite</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Invite <span className="text-primary">{profile.displayName}</span> to confirm this gig.
            They have a deadline to respond. Once they confirm, WhatsApp contact is unlocked.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Fee */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground flex items-center gap-2">
              <Euro className="h-4 w-4" />
              Fee (€)
            </Label>
            <Input
              type="number"
              min="0"
              step="1"
              value={formData.fee}
              onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
              required
              className="bg-secondary/50 border-border"
              data-testid="invite-fee-input"
            />
          </div>

          {/* Call Time */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Call Time / Show Start
            </Label>
            <Input
              type="time"
              value={formData.callTime}
              onChange={(e) => setFormData({ ...formData, callTime: e.target.value })}
              className="bg-secondary/50 border-border"
              data-testid="invite-calltime-input"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Notes (optional)
            </Label>
            <Textarea
              placeholder="Any specific requirements or instructions..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="bg-secondary/50 border-border"
              rows={3}
              data-testid="invite-notes-input"
            />
          </div>

          {/* Info Box */}
          <div className="p-3 rounded-lg bg-primary/10 border border-primary/30">
            <p className="text-xs text-primary">
              Talent has a response deadline based on urgency (2h urgent / 24h planned).
              On confirmation, WhatsApp contact is automatically unlocked.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-border"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary text-primary-foreground"
              data-testid="submit-send-invite"
            >
              {loading ? 'Sending...' : 'Send Invite'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
